import React, { useState, useCallback, useImperativeHandle, useEffect } from "react";
import ConfirmDialog from "../common/ConfirmDialog";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { httpsCallable, functions, getFigmaTokenForMember, updateFigmaTokensOnly, getGroupFigmaFile } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { FaSpinner, FaUser, FaCodeBranch, FaFileAlt } from "react-icons/fa";
import { MdWarning, MdDelete, MdKeyboardArrowDown, MdGroup, MdWarningAmber } from "react-icons/md";
import { TbReload } from "react-icons/tb";
import { PDFDocument } from "pdf-lib";

function FigmaPreview({ previewUrl }) {
  const [previewFailed, setPreviewFailed] = useState(false);
  if (!previewUrl) return null;
  return (

    <Section>
      <Label>Figma Preview</Label>

      {previewFailed ? (
        <FallbackBox>
          No preview available.{" "}
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            Open Figma in new tab
          </a>
        </FallbackBox>

      ) : (
        <>
          <StyledIframe
            src={previewUrl}
            title="Figma Preview"
            width="100%"
            height="600"
            allow="autoplay"
            onError={() => setPreviewFailed(true)}
          />
          <small>
            Can’t see it?{" "}
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              Open in new tab
            </a>
          </small>
        </>
      )}

    </Section>
  );
}

const FigmaLinkSection = (props, ref) => {
  const {
    docId, //[DEBUG]
    fileUuid, //[DEBUG]
    figmaLink,
    initialMeta,
    onChange,
    onMetaFetched,
    onRemove,
    isDuplicate,
    canRemove,
    isEmpty,
    onFetchStart,
  } = props;
  const { user, groupInfo, groupMembers } = useAuth();
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const toggleExpanded = () => setExpanded((e) => !e);
  const [formatError, setFormatError] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [combinedPdf, setCombinedPdf] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // --- Parse link ---
  const link = (figmaLink || "").trim();
  const match = link.match(/figma\.com\/(?:file|board|design|slide|proto)\/([a-zA-Z0-9]+)\b/);
  const fileId = match?.[1];


  const canFetch = !!link && !!match && !isDuplicate;

  // Detect Figma file type from URL
  const typeMatch = link.match(/figma\.com\/(file|board|design|slide|proto)\//);
  const fileTypeFromUrl = (() => {
    if (!typeMatch) return "unknown";
    const segment = typeMatch[1];
    switch (segment) {
      case "board": return "board";
      case "slide": return "slide";
      case "proto": return "prototype";
      case "design":
      case "file":
        return "design";
      default:
        return "unknown";
    }
  })();

  const disabledReason = !link
    ? "Cannot fetch an empty link"
    : !match
      ? "Invalid Figma link. Must be https://www.figma.com/<type>/<ID>/..."
      : isDuplicate
        ? "This Figma file is already added"
        : null;

  // If the parent updates meta (like switching sessions), sync it:
  useEffect(() => {
    if (initialMeta && Object.keys(initialMeta).length > 0) {
      setMeta(initialMeta);
    }
  }, [initialMeta]);

  useEffect(() => {
    if (!link) {
      setFormatError(null);
      return;
    } else if (!match) {
      setFormatError("❗ Invalid Figma link. Format: https://www.figma.com/file/<ID>");
    } else if (isDuplicate) {
      setFormatError("DUPLICATE");
    } else {
      setFormatError(null);
    }
  }, [link, match, isDuplicate]);


  const handleFetch = useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    if (!canFetch) {
      setMeta(null);
      if (!link) {
        setFetchError("❗ Cannot fetch an empty link. Please enter a Figma file URL.");
      } else if (!match) {
        setFetchError("❗ Invalid Figma link. Expected format: https://www.figma.com/file/<ID>");
      } else if (isDuplicate) {
        setFetchError("DUPLICATE");
      }
      setLoading(false);
      return { ok: false };
    }

    try {
      // 1. Get tokens from Firestore for this member
      const { access_token, refresh_token } = await getFigmaTokenForMember(groupInfo.groupId, user.email);

      const previous = await getGroupFigmaFile(groupInfo.groupId, fileId);

      // Find the last version id if have
      let lastVersionId = null;
      if (previous.found) {
        const versionIds = previous.versionIds;
        const lastIndex = Math.max(...Object.keys(versionIds).map(Number));
        lastVersionId = versionIds[lastIndex];
      }

      // 2. Fetch file metadata + versions (pass cached PDF URLs so the function can skip
      //    re-exporting if the Figma version hasn't changed)
      const fetchFigmaData = httpsCallable(functions, "fetchFigmaFileData");
      const res = await fetchFigmaData({
        access_token: access_token,
        refresh_token: refresh_token,
        file_id: fileId,
        last_version_id: lastVersionId,
        cached_pdf_urls: previous.pdfUrls || [],
        cached_file_name: previous.fileName || null,
        cached_last_modified: previous.lastModified || null,
      });

      const { fileData, updatedTokens } = res.data || {};
      if (!fileData) throw new Error("No file data returned");

      // 3. Save refreshed tokens if backend refreshed them
      if (updatedTokens?.access_token && updatedTokens?.expires_at) {
        await updateFigmaTokensOnly(groupInfo.groupId, user.email, {
          access_token: updatedTokens.access_token,
          refresh_token: updatedTokens.refresh_token,
          expires_at: updatedTokens.expires_at,
        });
      }

      let versionChange = { status: "error", count: 0 };

      if (lastVersionId) {
        if (fileData.latestVersionId === lastVersionId) {
          versionChange = { status: "no_change", count: 0 };
        } else {
          // current version is newer than last saved one
          versionChange = { status: "updated", count: fileData.versionCount || 1 };
        }
      } else {
        versionChange = { status: "initial", count: fileData.versionCount || 1 };
      }

      // const collaborators = Object.keys(fileData.editors || {});

      if (fileData.pdfUrls?.length) {
        try {
          const merged = await PDFDocument.create();

          for (const { url } of fileData.pdfUrls) {
            const bytes = await fetch(url).then((r) => r.arrayBuffer());
            const pdf = await PDFDocument.load(bytes);
            const copied = await merged.copyPages(pdf, pdf.getPageIndices());
            copied.forEach((p) => merged.addPage(p));
          }

          const blob = new Blob([await merged.save()], { type: "application/pdf" });
          const preview = URL.createObjectURL(blob);
          setCombinedPdf(blob);
          setPreviewUrl(preview);
        } catch (mergeErr) {
          console.error("❌ Error combining PDFs:", mergeErr);
        }
      }

      const metaData = {
        fileName: fileData.fileName,
        editors: fileData.editors,
        lastModified: fileData.lastModified,
        fileType: fileTypeFromUrl,
        latestVersionId: fileData.latestVersionId,
        versionChange,
        pdfUrls: fileData.pdfUrls,
      };

      setMeta(metaData);
      onMetaFetched?.(fileUuid, metaData);
      return { ok: true, meta: metaData };
    } catch (err) {
      console.error("❌ Figma fetch error:", err);
      if (err.code === "functions/permission-denied") {
        setFetchError("❗ Access denied. Ensure you have permission to view this file.");
      } else if (err.code === "functions/not-found") {
        setFetchError("❗ File not found. Please check the link and try again.");
      } else if (err.message?.toLowerCase().includes("token")) {
        setFetchError("❗ Figma token expired or missing. Please reconnect your Figma account.");
      } else {
        setFetchError("❗ Failed to fetch Figma data. Please try again later.");
      }
      setMeta(null);
      return { ok: false, error: err };
    } finally {
      setLoading(false);
    }
  }, [figmaLink, onMetaFetched]);


  const resetFigmaField = useCallback(() => {
    onMetaFetched(null);
    setMeta(null);
  }, [onChange, onMetaFetched]);


  useImperativeHandle(ref, () => ({
    fetchNow: handleFetch,
    reset: () => { setMeta(null); setFetchError(null); },
    combinedPdf,
    meta,
    fileUuid, // ADD THIS: Expose the fileUuid for debugging
  }), [handleFetch, combinedPdf, meta, fileUuid]);

  return (
    <DocSection>
      <InputRow>
        <InputWithLoading>
          <DocInput
            type="url"
            placeholder="https://www.figma.com/[file_type]/..."
            value={figmaLink || ""}
            onChange={(e) => onChange(fileUuid, e.target.value)}
          />
          {loading && <LoadingBar />}
        </InputWithLoading>

        <ButtonsInline>
          <IconButton
            type="button"
            onClick={() => {
              if (onFetchStart) onFetchStart();
              handleFetch();
            }}
            disabled={loading || !canFetch}
            title={disabledReason || "Fetch Figma Info"}
          >
            {loading ? <FaSpinner className="spin" /> : !canFetch ? <MdWarning /> : <TbReload />}
          </IconButton>

          {canRemove && isEmpty && (
            <RemoveInlineButton disabled={loading} onClick={() => onRemove?.(fileUuid)}>
              <MdDelete />
            </RemoveInlineButton>
          )}
        </ButtonsInline>
      </InputRow>

      {/* Error Display */}
      {(formatError || fetchError) && (
        <ErrorMessage>
          {formatError === "DUPLICATE" ? (
            <>
              ❗ This Figma file is already added.{" "}
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "underline" }}
              >
                View File
              </a>
            </>
          ) : (
            <>
              {formatError && <>{formatError}</>}
              {fetchError && !formatError && <>{fetchError}</>}
            </>
          )}
        </ErrorMessage>
      )}

      {fetchError && (
        <>
          {/* Case 1: Missing or expired token */}
          {fetchError.includes("Figma token expired or missing") && (
            <Card>
              <WarningBox>
                <WarningIcon />
                <WarningText>
                  <p>
                    Your Figma account is not connected or the token has expired. Please{" "}
                    <Strong>connect or reconnect</Strong> your account in the{" "}
                    <Link
                      to="/figma-setup"
                      style={{
                        textDecoration: "underline",
                        color: "#2563eb",
                        fontWeight: 500,
                      }}
                    >
                      Figma Connection Page
                    </Link>
                    .
                  </p>
                </WarningText>
              </WarningBox>
            </Card>
          )}

          {/* Case 2: Access denied */}
          {fetchError.includes("Access denied") && (
            <Card>
              <WarningBox>
                <WarningIcon />
                <WarningText>
                  <p>
                    Access denied. If this Figma file is from a <Strong>private workspace</Strong>, make sure:
                  </p>
                  <List>
                    <li>
                      Your account is <Strong>connected</Strong> in the{" "}
                      <Link
                        to="/figma-setup"
                        style={{
                          textDecoration: "underline",
                          color: "#2563eb",
                          fontWeight: 500,
                        }}
                      >
                        Figma Connection Page
                      </Link>
                      .
                    </li>
                    <li>
                      The file’s share settings is set to{" "}
                      <Strong>“view only” for anyone with the link</Strong>.
                    </li>
                  </List>
                </WarningText>
              </WarningBox>
            </Card>
          )}
        </>
      )}



      {meta && !fetchError && (
        <ToggleButton
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={`figma-card-${docId}`}
          title={expanded ? "Collapse details" : "Expand details"}
        >
          <ChevronIcon $open={expanded} />
          <span>{expanded ? "Hide" : "Show"} fetched details</span>
        </ToggleButton>
      )}

      {/* Show fetched Figma metadata in card */}
      {meta && Object.keys(meta).length > 0 && (
        <Collapsible
          id={`figma-card-${docId}`}
          aria-hidden={!expanded}
          $open={expanded}
        >
          <Card>
            <TitleRow>
              <Label>Figma File</Label>
              <GoogleDocLinkAnchor href={meta?.pdfStorage?.url || link} target="_blank" rel="noopener noreferrer">
                View Figma
              </GoogleDocLinkAnchor>
            </TitleRow>

            <Header>{meta.fileName}</Header>

            <Section>
              <Label>Figma Metadata</Label>
              <MetaGrid>
                <MetaBox color={{ bg: "#fef2f2", border: "#fecaca", text: "#dc2626", value: "#b91c1c" }}>
                  <IconWrapper><FaUser size={22} /></IconWrapper>
                  <MetaText color={{ text: "#dc2626", value: "#b91c1c" }}>
                    {meta.versionChange?.status === "initial" && (
                      <>
                        <p>Version Status</p>
                        <p>First submission</p>
                      </>
                    )}

                    {meta.versionChange?.status === "no_change" && (
                      <>
                        <p>Version Status</p>
                        <p>Same</p>
                      </>
                    )}

                    {meta.versionChange?.status === "updated" && (
                      <>
                        <p>Version Status</p>
                        <p>New version{meta.versionChange.count > 1 ? "s" : ""}</p>
                      </>
                    )}
                  </MetaText>
                </MetaBox>

                <MetaBox color={{ bg: "#ecfdf5", border: "#bbf7d0", text: "#10b981", value: "#047857" }}>
                  <IconWrapper><FaFileAlt size={22} /></IconWrapper>
                  <MetaText color={{ text: "#10b981", value: "#047857" }}>
                    <p>Figma Type</p>
                    <p>{meta.fileType.charAt(0).toUpperCase() + meta.fileType.slice(1)}</p>
                  </MetaText>
                </MetaBox>

                <MetaBox color={{ bg: "#faf5ff", border: "#e9d5ff", text: "#7e22ce", value: "#6b21a8" }}>
                  <IconWrapper><FaCodeBranch size={22} /></IconWrapper>
                  <MetaText color={{ text: "#7e22ce", value: "#6b21a8" }}>
                    <p>Edit Count{meta.versionChange.count > 1 ? "s" : ""}</p>
                    <p>{meta.versionChange.count}</p>
                  </MetaText>
                </MetaBox>

                {meta.editors && Object.keys(meta.editors).length > 0 && (
                  <MetaBox full color={{ bg: "#fefce8", border: "#fef08a", text: "#eab308", value: "#a16207" }}>
                    <IconWrapper>
                      <MdGroup className="text-yellow-600 mr-3" size={24} />
                    </IconWrapper>
                    <MetaText color={{ text: "#facc15", value: "#ca8a04" }}>
                      <p>
                        Figma Collaborator{Object.entries(meta.editors).length !== 1 ? 's' : ''}
                      </p>
                      <CollaboratorList>
                        {Object.entries(meta.editors).map(([name, count], i) => {
                          const nameStr = String(name || ""); // ensure it’s always a string

                          const member = groupMembers?.find(
                            (m) =>
                              m.figma?.handle?.toLowerCase?.() === nameStr.toLowerCase() ||
                              m.name?.toLowerCase?.() === nameStr.toLowerCase() ||
                              m.email?.toLowerCase?.() === nameStr.toLowerCase()
                          );

                          const isUnauthorized = !member;

                          return (
                            <ContributorRow key={i}>
                              <span style={{ color: isUnauthorized ? "#dc2626" : "inherit" }}>
                                {member
                                  ? `${member.name} (${count} edits)`
                                  : `Unauthorized User (${nameStr} – ${count} edits)`}
                              </span>
                            </ContributorRow>
                          );
                        })}

                      </CollaboratorList>
                    </MetaText>
                  </MetaBox>
                )}

              </MetaGrid>
            </Section>

            {(meta?.pdfStorage?.url || previewUrl) && (
              <FigmaPreview previewUrl={meta.pdfStorage?.url || previewUrl} />
            )}

            <Section>
              <RemoveButtonWrapper>
                <RemoveButton
                  type="button"
                  onClick={() => setShowRemoveConfirm(true)}
                >
                  Remove Figma
                </RemoveButton>
              </RemoveButtonWrapper>
            </Section>
          </Card>
        </Collapsible>
      )}
      {showRemoveConfirm && (
        <ConfirmDialog
          title="Remove Figma File?"
          message="Are you sure you want to remove this Figma info? This action cannot be undone."
          confirmText="Yes, Remove"
          cancelText="No, Go Back"
          variant="danger"
          onConfirm={() => { setShowRemoveConfirm(false); resetFigmaField(); onRemove(fileUuid); }}
          onCancel={() => setShowRemoveConfirm(false)}
        />
      )}
    </DocSection>
  );
};

const FigmaLinkSectionWithRef = React.forwardRef(FigmaLinkSection);

export default React.memo(FigmaLinkSectionWithRef);

const DocSection = styled.div`
   margin-top: 8px;
   margin-bottom: 8px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const DocInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  font-size: 14px;
  height: 48px; 
  border-radius: 10px;
  border: 1px solid #e4e4e4;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border: 2px solid #534af5;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
`;

// Doc Meta Card
const Card = styled.div`
  background: white;
  padding: 2rem 2rem 1rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  margin-top: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  background-color: #ffffff; 
  border: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const GoogleDocLinkAnchor = styled.a`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0b99ff;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Label = styled.small`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280; /* Tailwind gray-500 */
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;


//Meta data
const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'full',
})`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
  grid-column: ${({ full }) => (full ? "span 2" : "auto")};
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const MetaText = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'small',
})`
  p:first-child {
    font-size: ${({ small }) => (small ? '0.875rem' : '0.875rem')};
    color: ${({ color }) => color.text};
    font-weight: 500;
  }

  p:last-child {
    font-size: ${({ small }) => (small ? '0.95rem' : '1.5rem')};
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;

const ContributorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
`;

const CollaboratorList = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937; /* Tailwind gray-800 */
`;

const FallbackBox = styled.div`
  padding: 1rem;
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  text-align: center;
  font-size: 0.95rem;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;


const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const RemoveButton = styled.button`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #fca5a5;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #fecaca;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5);
  }
`;

const LoadingBar = styled.div`
  height: 4px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background-color: #3b82f6;
  animation: growProgress 1.5s ease-out infinite;
  transform-origin: center;


  @keyframes growProgress {
    0% { width: 0%; opacity: 0.4; }
    50% { width: 60%; opacity: 1; }
    100% { width: 95%; opacity: 0.4; }
  }
`;

const InputWithLoading = styled.div`
    position: relative;
    flex: 1; /* this ensures it expands to fill remaining space in InputRow */
    flex-direction: column;
    display: flex;
`;

const ButtonsInline = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const RemoveInlineButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid transparent;
  background: transparent;
  color: #ff4c4c;
  height: 100%;
  cursor: pointer;
  transition: color 0.2s ease;

  /* Keep the SVG at an exact pixel size */
  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  &:hover {
    color:rgb(195, 3, 3);
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  border-radius: 10px;
  border: 1px solid #e4e4e4;
  background: #534af5;
  cursor: pointer;
  color: white;
  padding: 12px;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover { 
    background: #3f35f6ff;
 }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg { width: 22px; height: 22px; transition: transform 0.2s ease;}

  &:hover svg {
    transform: scale(1.2); /* zoom only the icon */
  }

  .spin {
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); } to { transform: rotate(360deg); }
  }
`;

const Collapsible = styled.div.withConfig({
  shouldForwardProp: (p) => p !== '$open',
})`
  overflow: hidden;
  transition: max-height 280ms ease, opacity 200ms ease;
  max-height: ${({ $open }) => ($open ? '2400px' : '0px')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  will-change: max-height, opacity;
`;

// Toggle button next to reload
const ToggleButton = styled.button`
  display: flex;
  margin-top: 8px;
  border: none;
  align-items: center;
  color: #534af5; /* text-gray-600 */
  background-color: #f3f4f6; /* bg-gray-100 */
  padding: 0.5rem 1rem; /* py-2 px-4 */
  border-radius: 0.5rem; /* rounded-lg */
  transition: color 0.2s ease;

  &:hover {
    color: #352cedff; /* hover:text-gray-800 */
  }

  span { 
    margin-left: 0.25rem; /* ml-2 */
    font-size: 0.875rem; /* text-sm */
    font-weight: 500; /* font-medium */ }
`;

const ChevronIcon = styled(MdKeyboardArrowDown).withConfig({
  shouldForwardProp: (p) => p !== '$open',
})`
  margin-left: -4px;
  font-size: 24px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const WarningBox = styled.div`
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  align-items: start;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const WarningText = styled.div`
  color: #b45309;
  font-size: 0.875rem;
`;

const Strong = styled.span`
  font-weight: 600;
`;

const List = styled.ol`
  margin-top: 0.25rem;
  list-style: decimal inside;
  font-size: 0.875rem;
  color: #b45309;

  li + li {
    margin-top: 0.25rem;
  }
`;

const WarningIcon = styled(MdWarningAmber)`
  font-size: 24px;
  color: #f59e0b; /* Tailwind's text-yellow-500 */
  vertical-align: middle;
`;


